import { AppInstanceShort, DevHubProductType, MayBeNull } from '@wpp-open/core'

import { NONE_CHANGED_APP_INSTANCES } from 'constants/templates'
import {
  AppInstanceEditTemplateRequest,
  AppInstanceNewTemplateRequest,
  AppInstanceRemoveTemplateRequest,
  AppInstanceTemplate,
  AppInstanceTemplateAction,
  AppInstanceTemplateRequest,
  Template,
  TemplateAppInstanceGroups,
  TemplateResponse,
} from 'types/templates/templates'
import { getAppInstanceName } from 'utils/appInstances'

export const mapAppInstancesShortToFormValues = (appInstances: AppInstanceShort[]): AppInstanceTemplate[] => {
  return appInstances.map(appInstance => {
    return {
      id: appInstance.id,
      name: getAppInstanceName(appInstance),
      status: appInstance.status,
      description: appInstance.description,
      shortDescription: appInstance.devhubMetadata.shortDescription,
      routePath: appInstance.devhubMetadata.version.routePath,
      appType: appInstance.devhubMetadata.version.config?.appType || DevHubProductType.NATIVE_APPLICATION,
      group: appInstance.group,
      subgroup: appInstance.subgroup,
      devhubAppId: appInstance.devhubAppId,
      devhubAppVersionId: appInstance.devhubAppVersionId,
      logoOriginal: appInstance.logoOriginal,
      logoThumbnail: appInstance.logoThumbnail,
      logoMeta: appInstance.devhubMetadata.logoMeta,
      customContext: appInstance.devhubMetadata.version.customContext,
      ownerEmail: appInstance.devhubMetadata.ownerEmail,
      ownerName: appInstance.devhubMetadata.ownerName,
      isTemplate: !!appInstance.devhubMetadata.isTemplate,
      tenantId: appInstance.tenantId,
      context: appInstance.context,
      position: appInstance.position,
      versionType: appInstance.devhubMetadata.version.versionType,
      appearances: appInstance.devhubMetadata.version.appearances,
      flags: appInstance.flags,
      action: AppInstanceTemplateAction.EXIST,
    }
  })
}

export const getDefaultFormValues = (template?: MayBeNull<TemplateResponse>): Template => {
  return {
    id: template?.id || '',
    name: template?.name || '',
    description: template?.description || '',
    appInstances: template?.appInstances ? mapAppInstancesShortToFormValues(template.appInstances) : [],
  }
}

export const mapAppInstancesToPayload = (
  appInstances: AppInstanceTemplate[],
): AppInstanceTemplateRequest[] | typeof NONE_CHANGED_APP_INSTANCES => {
  const appInstancesEditedList = appInstances.filter(({ action }) => action !== AppInstanceTemplateAction.EXIST)

  if (appInstances.length && !appInstancesEditedList.length) return NONE_CHANGED_APP_INSTANCES

  return appInstancesEditedList.map(
    ({
      name,
      devhubAppId,
      devhubAppVersionId,
      context,
      osRoute,
      logoThumbnail,
      logoOriginal,
      flags,
      description,
      group,
      subgroup,
      position,
      action,
      id,
    }) => {
      if (action === AppInstanceTemplateAction.CREATE_NEW) {
        return {
          name,
          devhubAppId,
          devhubAppVersionId,
          context: context || undefined,
          osRoute: osRoute || undefined,
          subgroupId: subgroup?.id || null,
          logoThumbnail: logoThumbnail?.key
            ? { key: logoThumbnail.key, name: logoThumbnail.name, size: logoThumbnail.size }
            : null,
          logoOriginal: logoOriginal?.key
            ? { key: logoOriginal.key, name: logoOriginal.name, size: logoOriginal.size }
            : null,
          flags,
          description,
          groupId: group?.id || null,
          action,
          position,
        } as AppInstanceNewTemplateRequest
      }

      if (action === AppInstanceTemplateAction.EDIT) {
        return {
          position,
          name,
          context: context || undefined,
          osRoute: osRoute || undefined,
          subgroupId: subgroup?.id || null,
          logoThumbnail: logoThumbnail?.key
            ? { key: logoThumbnail.key, name: logoThumbnail.name, size: logoThumbnail.size }
            : null,
          logoOriginal: logoOriginal?.key
            ? { key: logoOriginal.key, name: logoOriginal.name, size: logoOriginal.size }
            : null,
          flags,
          description,
          groupId: group?.id || null,
          id,
          action,
        } as AppInstanceEditTemplateRequest
      }

      return {
        action,
        id,
      } as AppInstanceRemoveTemplateRequest
    },
  )
}

export enum ReorderedItemType {
  Groups = 'groups',
  Subgroups = 'subgroups',
}

export interface GroupOrderedItem {
  id: string
  order: number
  groupId?: string
}

interface UpdatePositionForGroupsProps {
  type: ReorderedItemType
  appInstances: AppInstanceTemplate[]
  groups?: TemplateAppInstanceGroups
  orderedItems: GroupOrderedItem[]
}

export const mapUpdatePositionForGroups = ({
  type,
  groups,
  appInstances,
  orderedItems,
}: UpdatePositionForGroupsProps): {
  updatedGroups: TemplateAppInstanceGroups
  updatedAppInstances: AppInstanceTemplate[]
} => {
  let subgroupsList = groups?.subgroups
  let groupsList = groups?.groups

  if (type === ReorderedItemType.Groups) {
    const groupIds = new Set(orderedItems.map(group => group.id))

    groupsList = [...(groupsList?.filter(group => !groupIds.has(group.id)) || []), ...orderedItems]
  }

  if (type === ReorderedItemType.Subgroups) {
    const subgroupIds = new Set(orderedItems.map(subgroup => `${subgroup.id}_${subgroup.groupId}`))

    subgroupsList = [
      ...(subgroupsList?.filter(subgroup => !subgroupIds.has(`${subgroup.id}_${subgroup.groupId}`)) || []),
      ...orderedItems,
    ]
  }

  const updatedGroups = {
    groups: groupsList,
    subgroups: subgroupsList,
  }

  const updatedAppInstances = appInstances.map(appInstance => {
    if (type === ReorderedItemType.Groups) {
      if (appInstance.group) {
        return {
          ...appInstance,
          group: {
            ...appInstance.group,
            order: orderedItems.filter(item => item.id === appInstance.group?.id)?.[0]?.order,
          },
        }
      }
      return appInstance
    } else {
      if (appInstance.group && appInstance.subgroup) {
        const [subgroup] = orderedItems.filter(
          item => item.id === appInstance.subgroup?.id && item?.groupId === appInstance.group?.id,
        )

        return {
          ...appInstance,
          subgroup: {
            ...appInstance.subgroup,
            order: subgroup?.order || appInstance.subgroup?.order,
          },
        }
      }
      return appInstance
    }
  })

  return { updatedGroups, updatedAppInstances }
}
